import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import {
  PrimitivesBluegray50,
  PrimitivesBluegray700,
  SemanticTokensColorTextBase,
  SemanticTokensColorBorderDividerLighter,
  SemanticTokensColorBorderButtonDangerSecondaryHover,
  SemanticTokensColorBgButtonDangerSecondaryHover,
} from '@ambulnz/design-tokens';
import { AUDIT_LOG_ITEM_KIND } from '../../../parsers/auditLog.parser';

const AuditLogList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const EntryAccordion = styled(Accordion)({
  background: PrimitivesBluegray50,
  borderRadius: '6px !important',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
});

const EntryAccordionSummary = styled(AccordionSummary)({
  padding: '8px 16px',
  minHeight: 48,
  background: PrimitivesBluegray50,
  borderRadius: 6,
  '&.Mui-expanded': {
    minHeight: 48,
    borderBottom: '1px solid #c8cdd6',
    borderRadius: '6px 6px 0 0',
  },
  '& .MuiAccordionSummary-content': { margin: 0, flexDirection: 'column' },
});

const EntryAccordionDetails = styled(AccordionDetails)({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

const EventRow = styled(Box)({
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
});

const EventDivider = styled(Divider)({
  width: '100%',
  borderColor: SemanticTokensColorBorderDividerLighter,
});

const LoadingRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 0',
  height: '40px',
});

const ErrorRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 16px',
  borderRadius: 6,
  background: SemanticTokensColorBgButtonDangerSecondaryHover,
  border: `1px solid ${SemanticTokensColorBorderButtonDangerSecondaryHover}`,
});

const ArrowDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.99595 12.7498C9.89595 12.7498 9.8022 12.7325 9.7147 12.6978C9.6272 12.663 9.54873 12.6109 9.47928 12.5415L5.52803 8.59025C5.37053 8.43275 5.29525 8.25331 5.3022 8.05192C5.30914 7.85053 5.389 7.67345 5.54178 7.52067C5.69456 7.36789 5.87164 7.2915 6.07303 7.2915C6.27442 7.2915 6.4515 7.36789 6.60428 7.52067L10.0001 10.9373L13.4168 7.52067C13.5696 7.36789 13.7466 7.29498 13.948 7.30192C14.1494 7.30887 14.3265 7.38873 14.4793 7.5415C14.6321 7.69428 14.7084 7.87137 14.7084 8.07275C14.7084 8.27414 14.6297 8.45317 14.4722 8.60984L10.5209 12.5415C10.4459 12.6109 10.3647 12.663 10.2772 12.6978C10.1897 12.7325 10.0959 12.7498 9.99595 12.7498Z"
      fill="#0D2B59"
    />
  </svg>
);

export const auditLogChangeShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
});

export const auditLogVerificationShape = PropTypes.shape({
  kind: PropTypes.oneOf([AUDIT_LOG_ITEM_KIND.VERIFICATION]).isRequired,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  header: PropTypes.string.isRequired,
  subtext: PropTypes.string,
  changes: PropTypes.arrayOf(auditLogChangeShape).isRequired,
});

export const auditLogAgentEventShape = PropTypes.shape({
  kind: PropTypes.oneOf([AUDIT_LOG_ITEM_KIND.AGENT_EVENT]).isRequired,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  header: PropTypes.string.isRequired,
  message: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
    }),
  ).isRequired,
});

export const auditLogEntryShape = PropTypes.oneOfType([auditLogVerificationShape, auditLogAgentEventShape]);

const EntryCard = ({ header, subtext, children }) => (
  <EntryAccordion>
    <EntryAccordionSummary expandIcon={<ArrowDownIcon />}>
      <Typography fontSize={14} fontWeight={600} color={SemanticTokensColorTextBase}>
        {header}
      </Typography>
      {subtext && (
        <Typography fontSize={12} fontWeight={400} color={PrimitivesBluegray700}>
          {subtext}
        </Typography>
      )}
    </EntryAccordionSummary>
    <EntryAccordionDetails>{children}</EntryAccordionDetails>
  </EntryAccordion>
);

EntryCard.propTypes = {
  header: PropTypes.string.isRequired,
  subtext: PropTypes.string,
  children: PropTypes.node,
};

const VerificationEntry = ({ entry }) => (
  <EntryCard header={entry.header} subtext={entry.subtext}>
    {entry.changes.map((change, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <EventDivider />}
        <EventRow>
          <Typography fontSize={14} fontWeight={400} color={SemanticTokensColorTextBase}>
            {change.label}
          </Typography>
        </EventRow>
      </React.Fragment>
    ))}
  </EntryCard>
);

VerificationEntry.propTypes = {
  entry: auditLogVerificationShape.isRequired,
};

const AgentEventEntry = ({ entry }) => {
  const rows = [
    ...(entry.message ? [{ label: null, value: entry.message }] : []),
    ...entry.details,
    ...(entry.message || entry.details.length ? [] : [{ label: null, value: 'No additional details' }]),
  ];

  return (
    <EntryCard header={entry.header}>
      {rows.map((row, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <EventDivider />}
          <EventRow sx={{ gap: '4px' }}>
            {row.label && (
              <Typography fontSize={14} fontWeight={600} color={SemanticTokensColorTextBase}>
                {`${row.label}:`}
              </Typography>
            )}
            <Typography fontSize={14} fontWeight={400} color={SemanticTokensColorTextBase}>
              {row.value}
            </Typography>
          </EventRow>
        </React.Fragment>
      ))}
    </EntryCard>
  );
};

AgentEventEntry.propTypes = {
  entry: auditLogAgentEventShape.isRequired,
};

const AuditLog = ({ entries, isLoading, error = null }) => (
  <AuditLogList>
    {isLoading && (
      <LoadingRow>
        <CircularProgress size={16} thickness={2.5} sx={{ color: '#5f6b7a' }} />
        <Typography fontSize={14} color="#5f6b7a">
          Loading entries…
        </Typography>
      </LoadingRow>
    )}

    {!isLoading && error && (
      <ErrorRow>
        <Typography fontSize={14} color="#a32d2d" sx={{ flex: 1 }}>
          Failed to load audit log entries.
        </Typography>
      </ErrorRow>
    )}

    {!isLoading &&
      !error &&
      entries.map(entry =>
        entry.kind === AUDIT_LOG_ITEM_KIND.AGENT_EVENT ? (
          <AgentEventEntry key={`${entry.kind}-${entry.id}`} entry={entry} />
        ) : (
          <VerificationEntry key={`${entry.kind}-${entry.id}`} entry={entry} />
        ),
      )}
  </AuditLogList>
);

AuditLog.propTypes = {
  entries: PropTypes.arrayOf(auditLogEntryShape).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
};

export default AuditLog;
